<template>
  <div class="simulator-page">
    <div class="notification-wrapper">
      <transition name="slide-fade">
        <div v-if="notification" class="notification" :class="notification.type">
          {{ notification.message }}
        </div>
      </transition>
    </div>
    <main class="simulator-content">
      <section class="intro">
        <p class="eyebrow">Circuit generator</p>
        <h1>Testa il prototipo di generazione dei circuiti</h1>
        <p class="subtitle">
          Compila il form per inviarci i parametri del tuo allenamento: simuleremo una chiamata al backend e ti notificheremo
          quando il file sarà pronto.
        </p>
      </section>

      <form class="simulator-form" @submit.prevent="submitForm">
        <div class="form-grid">
          <div class="form-group">
            <label for="duration">Durata</label>
            <select id="duration" v-model="form.duration" required>
              <option disabled value="">Seleziona la durata</option>
              <option v-for="option in durations" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="sport-specific">Sport specifico?</label>
            <select id="sport-specific" v-model="form.isSportSpecific" @change="handleSportSpecificChange" required>
              <option value="si">Sì</option>
              <option value="no">No</option>
            </select>
          </div>

          <div class="form-group" v-if="form.isSportSpecific === 'si'">
            <label for="sport">Sport</label>
            <select
              id="sport"
              v-model="form.sport"
              :required="form.isSportSpecific === 'si'"
            >
              <option disabled value="">Scegli lo sport</option>
              <option v-for="sport in sports" :key="sport" :value="sport">
                {{ sport }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="age-range">Età media degli atleti</label>
            <select id="age-range" v-model="form.ageRange" required>
              <option disabled value="">Seleziona la fascia d'età</option>
              <option v-for="option in ageRanges" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="environment">Ambiente</label>
            <select id="environment" v-model="form.environment" required>
              <option disabled value="">Scegli l'ambiente</option>
              <option v-for="option in environments" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </div>

          <div class="form-group full-width">
            <label for="tools">Tool a disposizione</label>
            <textarea
              id="tools"
              v-model="form.tools"
              rows="4"
              placeholder="Elenca gli strumenti o accessori disponibili..."
            ></textarea>
          </div>
        </div>

        <button type="submit" class="primary-button" :disabled="isSubmitting">
          {{ isSubmitting ? 'Invio in corso...' : 'Genera circuito' }}
        </button>
      </form>

      <div v-if="showFeedbackCta" class="feedback-cta">
        <p>Il circuito è pronto! Ti va di dirci cosa ne pensi del prototipo?</p>
        <a
          class="secondary-button"
          href="mailto:feedback@sportemocracy.com?subject=Feedback%20generatore%20circuiti"
        >
          Invia il tuo feedback
        </a>
      </div>
    </main>
    <Footer />
  </div>
</template>

<script>
import Footer from '@/components/Footer.vue';

export default {
  name: 'SimulatorView',
  components: {
    Footer
  },
  data() {
    return {
      form: {
        duration: '',
        isSportSpecific: 'no',
        sport: '',
        ageRange: '',
        environment: '',
        tools: ''
      },
      durations: ['30min', '45min', '60min', '90min'],
      sports: ['Atletica', 'Calcio', 'Pallacanestro', 'Pallavolo', 'Rugby'],
      ageRanges: ['10-13', '14-17', '18-35', '35+'],
      environments: ['Palestra', 'Indoor', 'Outdoor'],
      isSubmitting: false,
      notification: null,
      notificationTimeoutId: null,
      showFeedbackCta: false
    };
  },
  methods: {
    handleSportSpecificChange() {
      if (this.form.isSportSpecific === 'no') {
        this.form.sport = '';
      }
    },
    showNotification(message, type = 'info') {
      if (this.notificationTimeoutId) {
        clearTimeout(this.notificationTimeoutId);
      }
      this.notification = { message, type };
      this.notificationTimeoutId = setTimeout(() => {
        this.notification = null;
        this.notificationTimeoutId = null;
      }, 5000);
    },
    async submitForm() {
      if (this.isSubmitting) return;
      this.showFeedbackCta = false;
      this.isSubmitting = true;
      this.showNotification('Il circuito è in fase di creazione...', 'info');

      try {
        await this.mockCircuitGeneration();
        this.showNotification('Il file è pronto per essere scaricato.', 'success');
        this.showFeedbackCta = true;
      } catch (error) {
        this.showNotification('Si è verificato un errore inatteso. Riprova più tardi.', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },
    mockCircuitGeneration() {
      const payload = {
        duration: this.form.duration,
        isSportSpecific: this.form.isSportSpecific === 'si',
        sport: this.form.isSportSpecific === 'si' ? this.form.sport : null,
        ageRange: this.form.ageRange,
        environment: this.form.environment,
        tools: this.form.tools
      };

      // Mock di una chiamata POST verso un ipotetico backend.
      return new Promise((resolve) => {
        console.info('POST /api/circuiti', JSON.stringify(payload));
        setTimeout(() => {
          resolve({ downloadUrl: '#', payload });
        }, 30000);
      });
    }
  },
  beforeUnmount() {
    if (this.notificationTimeoutId) {
      clearTimeout(this.notificationTimeoutId);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/colors.scss';

.simulator-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: $neutral-light;
}

.simulator-content {
  flex: 1;
  width: min(960px, 100%);
  margin: 0 auto;
  padding: calc(var(--header-height) + 2rem) 1.5rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.intro {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.8rem;
  color: $primary;
  margin-bottom: 0.25rem;
}

h1 {
  font-size: clamp(2rem, 4vw, 2.8rem);
  color: $primary;
  margin: 0;
}

.subtitle {
  font-size: 1.05rem;
  color: $secondary-cold;
  margin: 0;
}

.simulator-form {
  background: white;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 60px rgba(17, 24, 39, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

label {
  font-weight: 600;
  color: $primary;
}

select,
textarea {
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  resize: vertical;
}

textarea {
  min-height: 120px;
}

select:focus,
textarea:focus {
  outline: 2px solid $primary-light;
  border-color: transparent;
}

.primary-button {
  align-self: flex-start;
  background: $primary;
  color: white;
  border: none;
  border-radius: 999px;
  padding: 0.9rem 2.25rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.primary-button:disabled {
  background: rgba(32, 34, 59, 0.4);
  cursor: not-allowed;
}

.primary-button:not(:disabled):hover {
  background: $primary;
  transform: translateY(-1px);
}

.feedback-cta {
  border: 1px solid rgba(32, 34, 59, 0.1);
  border-radius: 18px;
  padding: 1.5rem;
  background: rgba(255, 128, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.secondary-button {
  align-self: flex-start;
  border-radius: 999px;
  border: 1px solid $primary;
  color: $primary;
  padding: 0.75rem 1.75rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
}

.secondary-button:hover {
  background: $primary;
  color: white;
}

.notification-wrapper {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 50;
}

.notification {
  min-width: 260px;
  max-width: 320px;
  padding: 1rem 1.25rem;
  border-radius: 16px;
  color: white;
  box-shadow: 0 20px 45px rgba(17, 24, 39, 0.2);
}

.notification.info {
  background: $primary;
}

.notification.success {
  background: #16a34a;
}

.notification.error {
  background: #dc2626;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 640px) {
  .simulator-form {
    padding: 1.5rem;
  }

  .notification-wrapper {
    right: 1rem;
    left: auto;
  }
}
</style>
